import api from './api';

const apiBaseUrl = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');

const hasPendingMasterProviderDocuments = (documents = []) => (
    Array.isArray(documents) && documents.some((document) => document?.file)
);

const buildMasterProviderRequestData = (data = {}) => {
    const documents = Array.isArray(data.documents) ? data.documents : [];
    const existingDocuments = documents
        .filter((document) => !document?.file)
        .map((document) => ({
            label: document?.label || '',
            original_name: document?.original_name || '',
            file_name: document?.file_name || '',
            file_path: document?.file_path || '',
            object_key: document?.object_key || '',
            mime_type: document?.mime_type || '',
            size: document?.size || 0,
            uploaded_at: document?.uploaded_at || null
        }));
    const pendingDocuments = documents.filter((document) => document?.file);

    const payload = {
        ...data,
        documents: existingDocuments,
        new_document_labels: pendingDocuments.map((document) => document?.label || '')
    };

    if (!hasPendingMasterProviderDocuments(documents)) {
        return payload;
    }

    const formData = new FormData();
    formData.append('payload', JSON.stringify(payload));
    pendingDocuments.forEach((document) => {
        formData.append('documents', document.file);
    });

    return formData;
};

const gameService = {
    // Currencies
    getCurrencies: async (params) => {
        const response = await api.get('/game/currencies', { params });
        return response.data;
    },

    createCurrency: async (data) => {
        const response = await api.post('/game/currencies', data);
        return response.data;
    },

    updateCurrency: async (id, data) => {
        const response = await api.put(`/game/currencies/${id}`, data);
        return response.data;
    },
    
    deleteCurrency: async (id) => {
        const response = await api.delete(`/game/currencies/${id}`);
        return response.data;
    },

    // Categories
    getCategories: async (params) => {
        const response = await api.get('/game/categories', { params });
        return response.data;
    },

    createCategory: async (data) => {
        const response = await api.post('/game/categories', data);
        return response.data;
    },

    updateCategory: async (id, data) => {
        const response = await api.put(`/game/categories/${id}`, data);
        return response.data;
    },
    
    deleteCategory: async (id) => {
        const response = await api.delete(`/game/categories/${id}`);
        return response.data;
    },

    // Sources
    getSources: async (params) => {
        const response = await api.get('/game/sources', { params });
        return response.data;
    },

    createSource: async (data) => {
        const response = await api.post('/game/sources', data);
        return response.data;
    },

    updateSource: async (id, data) => {
        const response = await api.put(`/game/sources/${id}`, data);
        return response.data;
    },
    
    deleteSource: async (id) => {
        const response = await api.delete(`/game/sources/${id}`);
        return response.data;
    },

    // Providers (Basic)
    getProviders: async (params) => {
        const response = await api.get('/game/providers', { params });
        return response.data;
    },

    createProvider: async (data) => {
        const response = await api.post('/game/providers', data);
        return response.data;
    },

    updateProvider: async (id, data) => {
        const response = await api.put(`/game/providers/${id}`, data);
        return response.data;
    },
    
    deleteProvider: async (id) => {
        const response = await api.delete(`/game/providers/${id}`);
        return response.data;
    },

    // Master Providers (L1 Configuration)
    getMasterProviders: async (params) => {
        const response = await api.get('/game/master-providers', { params });
        return response.data;
    },

    exportMasterProviders: async () => {
        const response = await api.get('/game/master-providers/export', {
            responseType: 'blob',
            timeout: 60000
        });
        return response.data;
    },

    getMasterProviderById: async (id) => {
        const response = await api.get(`/game/master-providers/${id}`);
        return response.data;
    },

    createMasterProvider: async (data) => {
        const requestData = buildMasterProviderRequestData(data);
        const response = await api.post('/game/master-providers', requestData, requestData instanceof FormData ? {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        } : undefined);
        return response.data;
    },

    updateMasterProvider: async (id, data) => {
        const requestData = buildMasterProviderRequestData(data);
        const response = await api.put(`/game/master-providers/${id}`, requestData, requestData instanceof FormData ? {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        } : undefined);
        return response.data;
    },

    deleteMasterProvider: async (id) => {
        const response = await api.delete(`/game/master-providers/${id}`);
        return response.data;
    },

    importMasterProviders: async ({
        file,
        mode = 'dry-run',
        confirmationToken,
        skipIssues = false,
        resolutions = {}
    }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mode', mode);
        formData.append('skip_issues', String(skipIssues));
        formData.append('resolutions', JSON.stringify(resolutions));
        if (confirmationToken) formData.append('confirmation_token', confirmationToken);

        const response = await api.post('/game/master-providers/import', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            timeout: 300000
        });
        return response.data;
    },

    getMasterProviderDocumentUrl: (document) => {
        if (!document?.file_path) return '#';
        if (/^https?:\/\//i.test(document.file_path)) return document.file_path;
        return `${apiBaseUrl}${document.file_path}`;
    },

    // Sub-Categories
    getSubCategories: async (params) => {
        const response = await api.get('/game/sub-categories', { params });
        return response.data;
    },

    createSubCategory: async (data) => {
        const response = await api.post('/game/sub-categories', data);
        return response.data;
    },

    updateSubCategory: async (id, data) => {
        const response = await api.put(`/game/sub-categories/${id}`, data);
        return response.data;
    },
    
    deleteSubCategory: async (id) => {
        const response = await api.delete(`/game/sub-categories/${id}`);
        return response.data;
    },
  // Country Methods
  getCountries: (params) => api.get('/game/countries', { params }).then(res => res.data),
  createCountry: (data) => api.post('/game/countries', data).then(res => res.data),
  updateCountry: (id, data) => api.put(`/game/countries/${id}`, data).then(res => res.data),
  deleteCountry: (id) => api.delete(`/game/countries/${id}`).then(res => res.data),
};

export default gameService;
